/**
 * Shared constants used across all storefront routes.
 */

// Canonical free-shipping threshold when the market has no override.
// Must match every other fallback in the codebase (announcement bar,
// checkout, cart, layout loader). Previously a three-way drift existed:
//   - this file said 500_000
//   - _storefront.tsx said 1_500_000 ("matches existing announcement-bar default")
//   - _storefront.cart.tsx fell back to this constant
// so when a market had no freeShippingThreshold set, the announcement bar
// promised free shipping over 1.5M while cart/checkout kicked in at 500K.
// Customers qualified at 600K but saw "add 900,000₫ more" messaging.
// The business actually uses 1.5M (see restore commit 6a2e7f5 restoring
// "1.500.000₫" copy on FAQ + Shipping pages), so that's the canonical.
export const FREE_SHIPPING_THRESHOLD = 1_500_000; // 1.500.000 ₫

/**
 * Comprehensive map of catalog color names to hex values.
 * Keys are exactly the strings parsed from product titles
 * (`Product Name - Color (Vietnamese)`), so they must stay
 * spelled the way they appear in the catalog.
 */
export const COLOR_HEX: Record<string, string> = {
  // Added 21 Oct 2026 (Contour "Smoky Navy" and Track Jacket "Green/White" rendered grey):
  "Buttermilk": "#F3E5AB",
  "Cyan Blue": "#2A9DD6",
  "Smoky Navy": "#3B4A66",
  "Smoky Blue": "#5B6B8A",
  "Charcoal": "#36454F",

  // ── Whites & creams ──
  White: "#FFFFFF",
  Snow: "#FFFAFA",
  "Snow White": "#FFFAFA",
  "Off White": "#FAF0E6",
  "Almond White": "#F5EFE0",
  "Milky White": "#FAF8F5",
  Milk: "#FAFAF0",
  Ivory: "#FFFFF0",
  "Cream White": "#F5F0E8",
  Cream: "#FFFDD0",
  Pearl: "#F0EAD6",
  "Oat Milk": "#EFE0C6",
  Oat: "#D4C5A9",
  "Ice Cream": "#F8E2D0",
  Butter: "#FFF8DC",
  "Butter Cream": "#FFF8E7",
  "Butter Yellow": "#FFE4A0",

  // ── Blacks & dark greys ──
  Black: "#000000",
  "Glossy Black": "#0A0A0A",
  "Smoky Black": "#100C08",
  "Black Leopard": "#1F1F1F",
  "Zebra Print": "#1F1F1F",
  Carbon: "#3A3A3A",
  Smoke: "#738276",

  // ── Greys ──
  Grey: "#808080",
  "Cool Grey": "#8C92AC",
  "Dark Grey": "#555555",
  "Light grey": "#D3D3D3",
  "Light Grey": "#D3D3D3",
  Silver: "#C0C0C0",
  Steel: "#7A8AA0",
  Gravel: "#7E7B6B",
  Ash: "#B2BEB5",
  Mixed: "#9A9A9A",
  Shadow: "#3F3F3F",

  // ── Browns / earth tones ──
  Brown: "#6B4226",
  "Golden Brown": "#996515",
  "Mossy Brown": "#5C4033",
  Brunette: "#5C3317",
  Camel: "#C19A6B",
  Caramel: "#C68E17",
  Coffee: "#6F4E37",
  Mocha: "#967259",
  Latte: "#C19A6B",
  Cacao: "#381F1A",
  Choco: "#4A2C2A",
  // 2026-05-25: Chocolate was #7B3F00 which renders as a rust /
  // burnt-orange — way too red for "chocolate". Bumped to a proper
  // dark milk-chocolate brown. (Audit feedback — fabric "chocolate"
  // is uniformly dark brown, never orange-tinted.)
  Chocolate: "#5C3A1E",
  Espresso: "#4A3728",
  Chestnut: "#954535",
  Pecan: "#C7A17A",
  Peanut: "#BC8F5C",
  Tan: "#D2B48C",
  Sand: "#C2B280",
  Beige: "#F5F5DC",
  Nude: "#E6BE8A",
  Earthy: "#8B7355",
  Khaki: "#8B7355",
  Kaki: "#C3B091",
  Raisin: "#482D2A",
  // 2026-05-25: bare "Taupe" was missing — Poise Leggings/Bra Taupe
  // fell back to grey. Classic greyish-brown taupe (literally what
  // the Vietnamese "Xám Nâu" subtitle says).
  Taupe: "#483C32",

  // ── Reds / pinks / corals ──
  // 2026-05-25: bare "Red" was missing — Flow Shorts/Bra Red fell back
  // to neutral grey. Saturated true red, brighter than Ruby/Burgundy
  // because the actual fabric is a pop-red activewear shade.
  Red: "#DC2626",
  Ruby: "#9B111E",
  "Ruby Red": "#9B111E",
  Cherry: "#DE3163",
  "Fire Red": "#B22222",
  Fire: "#B22222",
  "Red Lipstick": "#C40233",
  "Red Wine": "#722F37",
  Burgundy: "#800020",
  Merlot: "#722F37",
  Plum: "#8E4585",
  Lava: "#CF1020",
  Scarlet: "#FF2400",
  "Strawberry Red": "#C41E3A",
  Strawberry: "#FC5A8D",
  // 2026-05-25: explicit "Strawberry Pink" entry — Monaco line uses
  // this name. The first-token fallback was matching "Strawberry"
  // (#FC5A8D, a bright pink-red) which is wrong; Strawberry Pink is
  // a softer, lighter pink reminiscent of strawberry-milk colour.
  "Strawberry Pink": "#F5A4B5",
  Pink: "#FFB6C1",
  PInk: "#FFB6C1",
  "Hot Pink": "#FF69B4",
  "Bright Pink": "#FF1493",
  "Pink Lemonade": "#FCB1B5",
  "Pink Red": "#ED143D",
  "Soft Pink": "#FFC2D1",
  "Glow Pink": "#FFB6C1",
  "Gossamer Pink": "#FFE4E1",
  "Lavender Pink": "#FBB1E1",
  "Misty Pink": "#F4C2C2",
  "Pastel Pink": "#FFD1DC",
  "Baby Pink": "#F4C2C2",
  "Tea Rose": "#F4C2C2",
  Rose: "#FF66A1",
  "Dusty Rose": "#C4A4A4",
  Blush: "#E8C4C4",
  // 2026-05-25: Mauve was #E0B0FF which is electric lavender — that's
  // never what a fabric "mauve" is. Real fabric mauve is a muted,
  // dusty pinkish-grey (think old-rose or vintage mauve). The
  // electric value was a CSS-named-color mismatch (CSS "mauve" is
  // sometimes mapped to a bright lilac, but textile-mauve is muted).
  Mauve: "#915F6D",
  Barbie: "#E0218A",
  "Barbie Pink": "#E0218A",
  Flamingo: "#FC8EAC",
  "Flamingo Pink": "#FC8EAC",
  Venus: "#F4C2C2",

  // ── Oranges / peaches ──
  Orange: "#FFA500",
  "Coral Orange": "#FF7F50",
  "Electric Orange": "#FF6700",
  "Melon Orange": "#FF8C5A",
  // 2026-09-21: Peach was #FFE5B4 (CSS "peach puff", a pale cream
  // apricot) — nothing like the fabric. EQL "Peach (Hồng Cam)" is a soft
  // coral-pink: sampled from the Shape Bra Peach PDP photo 2026-09-21
  // (fabric average #EABDB1, nudged a touch more saturated so the swatch
  // reads on white). Staff sheet r50.
  Peach: "#F3B9A8",
  "Bright Peach": "#FFA07A",
  Apricot: "#FBCEB1",
  Sunrise: "#FF8B6F",
  Bronze: "#CD7F32",
  Rust: "#B7410E",

  // ── Yellows ──
  Yellow: "#F0C75E",
  "Lemon Yellow": "#FFF44F",
  // 2026-05-25: bare "Lemon" added when Monaco Top/Skorts Lemon
  // launched. The actual fabric is a soft sunny pastel yellow
  // (Vàng Chanh in VN) — not the electric #FFF44F of "Lemon Yellow".
  // Picked to match the garment photo. Without this entry the swatch
  // fell back to neutral grey.
  Lemon: "#FCEE91",
  Mustard: "#D4A017",
  Bumblebee: "#FFC125",
  "Yellow/White": "#FFF8B0",

  // ── Greens ──
  Green: "#2E8B57",
  "Dark Green": "#0F5132",
  "Forest Green": "#228B22",
  "Pine Green": "#01796F",
  "Emerald Green": "#50C878",
  "Lime Green": "#32CD32",
  // EQL "Lime" is a soft buttery pastel lime-yellow (Finesse - Lime),
  // not the electric chartreuse #BFFF00 the previous value rendered as.
  // Photo reference: pale sunny yellow-green, similar to Lululemon's
  // "Limeade" or Aritzia's "Sunny Citrus". Picked to match the actual
  // garment colour on the storefront swatch. (Merchant feedback 2026-05-08.)
  Lime: "#E5E58E",
  "Neon Green": "#39FF14",
  "Pastel Green": "#B6E2C7",
  "Pastel Mint": "#C8E6C9",
  Mint: "#98FF98",
  Matcha: "#BDC68C",
  "Green Tea": "#B5C18E",
  "Green Smoke": "#76896C",
  "Grey Green": "#8F9779",
  "Shadow Green": "#4F6F52",
  "Teal Green": "#008080",
  Pistachio: "#93C572",
  Avocado: "#568203",
  Olive: "#6B6B17",
  "Dark Olive": "#3C4A2E",
  Moss: "#8A9A5B",
  Sage: "#9CAF88",
  Alien: "#6FAB6F",
  // 2026-05-11: Pine added when the Aura Pine collection launched
  // (`aura-top-pine`, `aura-skorts-pine`). Hex matches the Xanh Cổ Vịt
  // duck-green of the actual fabric — a warm dark teal, NOT the
  // canonical #01796F web "pine green" which reads too blue.
  Pine: "#2D5A4F",

  // ── Blues ──
  Blue: "#1E54B7",
  // 2026-06-11: Azure added with the Sculpt launch ("Xanh Thanh Thiên").
  // Hex sampled from the actual PDP photography (chest #016181 / hip
  // #02587B) — the fabric is a deep teal-petrol, NOT the pale sky blue
  // CSS azure implies. Without this entry the new Sculpt swatches
  // rendered fallback grey.
  Azure: "#0A6080",
  // 2026-05-25 (round 2): Blueberry corrected from the original
  // #4F4E8B blueish-purple guess to a clean teal/sky blue matching
  // the actual Monaco Summer banner photography (model wears a
  // medium-saturation cyan tennis dress). "Xanh Dương" in the
  // Vietnamese subtitle literally just means "blue" — the original
  // hex over-shifted toward purple because of the fruit name.
  Blueberry: "#5BA8C8",
  "Navy Blue": "#000080",
  Navy: "#1B2A4A",
  Midnight: "#191970",
  Cobalt: "#0047AB",
  "Cobalt Blue": "#0047AB",
  Saphire: "#0F52BA",
  Sapphire: "#0F52BA",
  Sky: "#87CEEB",
  "Sky Blue": "#87CEEB",
  "Baby Blue": "#89CFF0",
  "Light Blue": "#ADD8E6",
  "Lake Blue": "#5B8BA0",
  Lagoon: "#4FA4B8",
  "Storm Blue": "#4F6D7A",
  "Blue Grey": "#6E7F80",
  Aqua: "#00CED1",
  Denim: "#1560BD",

  // ── Purples / violets ──
  Purple: "#6B3FA0",
  Violet: "#8F00FF",
  Lilac: "#C8A2C8",
  Lavender: "#B57EDC",
  Orchid: "#DA70D6",
  "Pastel Purple": "#B19CD9",
  Taro: "#B19CD9",
  // 2026-05-25: bare "Grape" was missing — Poise Leggings/Bra Grape
  // fell back to grey. Deep grape-juice purple matching the fabric.
  Grape: "#6F2DA8",
};

/**
 * Garment / size suffixes that may follow a colour name in legacy
 * product titles, e.g. "Black SKIRT", "Brown TOP", "Tím - M".
 * Stripped during normalization so the underlying colour still resolves.
 */
const GARMENT_SUFFIX_RE = /\s+(skirt|skort|top|tee|tank|jacket|set|short|shorts|pant|pants|legging|leggings|bra|sock|socks|m|s|l|xl|xs|xxl)$/i;

/**
 * Resolve a hex value for a catalog colour string.
 * Tries exact match → trimmed → garment suffix stripped → title case →
 * first-token. Returns a neutral grey if nothing matches so swatches
 * never disappear entirely.
 */
/** Sapo often names colours in Vietnamese ("Xanh Navy Khói", "Nâu Kaki") — map those to the English keys above. */
const VI_COLOR_ALIASES: Record<string, string> = {
  "đen": "Black", "trắng": "White", "trắng kem": "Cream", "kem": "Cream", "be": "Beige",
  "xanh navy": "Navy", "navy": "Navy", "xanh navy khói": "Smoky Navy", "navy khói": "Smoky Navy", "xanh dương": "Blue",
  "xanh lơ": "Cyan Blue", "xanh thanh thiên": "Azure", "xanh cổ vịt": "Pine", "xanh rêu": "Moss", "xanh lá": "Green",
  "xanh mint": "Mint", "xanh bạc hà": "Mint", "xanh pastel": "Pastel Mint",
  "nâu": "Brown", "nâu kaki": "Kaki", "kaki": "Kaki", "nâu đậm": "Chocolate", "cà phê": "Coffee",
  "hồng": "Pink", "hồng cam": "Peach", "hồng pastel": "Pastel Pink", "hồng đậm": "Hot Pink", "hồng nhạt": "Soft Pink",
  "tím": "Purple", "tím đậm": "Raisin", "tím nho": "Grape", "xám": "Grey", "xám nâu": "Taupe", "xám đậm": "Charcoal",
  "vàng": "Yellow", "vàng kem": "Buttermilk", "vàng chanh": "Lemon", "vàng bơ": "Butter", "cam": "Orange", "đỏ": "Red",
  "đỏ đô": "Burgundy", "đỏ cherry": "Cherry", "bạc": "Silver", "cát": "Sand", "ô liu": "Olive", "rêu": "Moss",
};
const TWO_TONE_SPLIT = /\s*(?:\/|&|\+|\bx\b|\bvà\b)\s*/i;

/** Lower-cased name without a parenthesised alias: "Kaki (Nâu Kaki)" → ["kaki", "nâu kaki"]. */
function colourNameParts(raw: string): string[] {
  const m = raw.trim().match(/^(.*?)\s*\(([^)]*)\)\s*$/);
  return m ? [m[1].trim(), m[2].trim()].filter(Boolean) : [raw.trim()];
}

function lookupColourHex(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  if (COLOR_HEX[trimmed]) return COLOR_HEX[trimmed];
  const stripped = trimmed.replace(GARMENT_SUFFIX_RE, "").trim();
  if (stripped !== trimmed && COLOR_HEX[stripped]) return COLOR_HEX[stripped];
  const titleCased = stripped
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
  if (COLOR_HEX[titleCased]) return COLOR_HEX[titleCased];
  const alias = VI_COLOR_ALIASES[stripped.toLowerCase()];
  if (alias && COLOR_HEX[alias]) return COLOR_HEX[alias];
  const words = titleCased.split(" ").filter(Boolean);
  // "Pink Floral" → Pink; "Smoky Navy" / "Cyan Blue" → the last word is the hue.
  if (words.length > 1) {
    if (COLOR_HEX[words[0]]) return COLOR_HEX[words[0]];
    const last = words[words.length - 1];
    if (COLOR_HEX[last]) return COLOR_HEX[last];
  }
  return null;
}

/**
 * Hex for one colour name. Tries the exact key, the garment-suffix-stripped
 * name, title case, the Vietnamese alias ("Xanh Navy Khói"), the alias in
 * parentheses ("Kaki (Nâu Kaki)"), then the first / last word. A two-tone
 * name ("Green/White") answers its first colour; getSwatchBackground()
 * draws both halves. Neutral grey when nothing matches, so swatches never
 * disappear.
 */
export function getColorHex(raw: string | null | undefined): string {
  if (!raw || !raw.trim()) return "#cccccc";
  for (const part of colourNameParts(raw)) {
    const first = part.split(TWO_TONE_SPLIT).filter(Boolean)[0] ?? part;
    const hex = lookupColourHex(first) ?? (first !== part ? lookupColourHex(part) : null);
    if (hex) return hex;
  }
  return "#cccccc";
}

/**
 * CSS background for a swatch: a hex, or a diagonal split for two-tone names
 * ("Green/White", "Black & White"). Use with `background`, not `background-color`.
 */
export function getSwatchBackground(raw: string | null | undefined): string {
  if (!raw) return "#cccccc";
  const [main] = colourNameParts(raw);
  const parts = main.split(TWO_TONE_SPLIT).filter(Boolean);
  if (parts.length >= 2) {
    const a = lookupColourHex(parts[0]);
    const b = lookupColourHex(parts[1]);
    if (a && b) return `linear-gradient(135deg, ${a} 0 50%, ${b} 50% 100%)`;
  }
  return getColorHex(raw);
}

export const SIZE_ORDER: Record<string, number> = {
  // `X` is a defensive alias for `XL` — variant data in prod 2026-05-11
  // had 2 products (whisper-choco-top, aura-plum-skorts) with
  // `option1 = "X"` instead of "XL". Without this alias, the
  // size-detection heuristic in ProductCard treats those variants as
  // colours and renders 4 fake colour swatches on the product card.
  // The data has since been corrected, but this alias prevents
  // future similar typos from breaking the UI.
  XXS: 0, XS: 1, S: 2, M: 3, L: 4, X: 5, XL: 5, XXL: 6, "2XL": 7, "3XL": 8,
  "One Size": 99, OS: 99, OSFA: 99,
};

// ─── Color families (Aritzia-style filter grouping) ─────────────────
//
// The catalog has 150+ individual colour names ("Plum", "Espresso",
// "Mahogany", "Choco", "Brunette", "Coffee", "Mocha", "Latte", …). A
// flat list of ~150 swatches is overwhelming and makes filter selection
// noisy. Aritzia (and most premium retailers) collapse to ~13 colour
// families with a 2-swatch preview showing the range within the family.
//
// Each entry below maps a family name → (a) two representative hex
// values for the preview, (b) the explicit list of catalogue colour
// strings that belong to it. Add new colour names to the right family
// when introducing them in COLOR_HEX above.
//
// The loader at /products expands a `?color=Brown` URL param to
// `colorIn ∈ family.colors` for the WHERE clause, so the database keeps
// using the granular colour strings while the UI surfaces only the
// family.

export interface ColorFamily {
  name: string; // Display name + URL param value
  swatches: [string, string]; // Light + dark representative hex
  colors: string[]; // Catalog colour names belonging to this family
}

export const COLOR_FAMILIES: ColorFamily[] = [
  {
    name: "Beige",
    swatches: ["#F5F5DC", "#C2B280"],
    colors: [
      "Almond White", "Beige", "Cream", "Cream White", "Ice Cream",
      "Ivory", "Milk", "Milky White", "Nude", "Oat", "Oat Milk",
      "Off White", "Pearl", "Sand",
    ],
  },
  {
    name: "Black",
    swatches: ["#3A3A3A", "#000000"],
    colors: [
      "Black", "Black Leopard", "Carbon", "Glossy Black",
      "Smoky Black", "Zebra Print", "Shadow",
    ],
  },
  {
    name: "Blue",
    swatches: ["#87CEEB", "#1E54B7"],
    colors: [
      "Aqua", "Baby Blue", "Blue", "Blue Grey", "Blueberry", "Cobalt",
      "Cobalt Blue", "Denim", "Lagoon", "Lake Blue", "Light Blue",
      "Midnight", "Navy", "Navy Blue", "Saphire", "Sapphire", "Sky",
      "Sky Blue", "Storm Blue",
    ],
  },
  {
    name: "Brown",
    swatches: ["#967259", "#4A2C2A"],
    colors: [
      "Brown", "Brunette", "Bronze", "Cacao", "Caramel", "Chestnut",
      "Choco", "Chocolate", "Coffee", "Earthy", "Espresso", "Golden Brown",
      "Latte", "Mocha", "Mossy Brown", "Raisin", "Rust", "Taupe",
    ],
  },
  {
    name: "Green",
    swatches: ["#9CAF88", "#0F5132"],
    colors: [
      "Alien", "Avocado", "Dark Green", "Dark Olive", "Emerald Green",
      "Forest Green", "Green", "Green Smoke", "Green Tea", "Grey Green",
      "Lime", "Lime Green", "Matcha", "Mint", "Moss", "Neon Green",
      "Olive", "Pastel Green", "Pastel Mint", "Pine Green", "Pistachio",
      "Sage", "Shadow Green", "Smoke", "Teal Green",
    ],
  },
  {
    name: "Grey",
    swatches: ["#D3D3D3", "#555555"],
    colors: [
      "Ash", "Cool Grey", "Dark Grey", "Gravel", "Grey", "Light Grey",
      "Light grey", "Mixed", "Silver", "Steel",
    ],
  },
  {
    name: "Orange",
    swatches: ["#FFE5B4", "#B7410E"],
    colors: [
      "Apricot", "Bright Peach", "Coral Orange", "Electric Orange",
      "Melon Orange", "Orange", "Peach", "Sunrise",
    ],
  },
  {
    name: "Pink",
    swatches: ["#FFC2D1", "#FF1493"],
    colors: [
      "Baby Pink", "Barbie", "Barbie Pink", "Blush", "Bright Pink",
      "Dusty Rose", "Flamingo", "Flamingo Pink", "Glow Pink",
      "Gossamer Pink", "Hot Pink", "Lavender Pink", "Mauve", "Misty Pink",
      "Pastel Pink", "Pink", "PInk", "Pink Lemonade", "Pink Red", "Rose",
      "Soft Pink", "Strawberry", "Strawberry Pink", "Tea Rose", "Venus",
    ],
  },
  {
    name: "Purple",
    swatches: ["#C8A2C8", "#6B3FA0"],
    colors: [
      "Grape", "Lavender", "Lilac", "Mauve", "Orchid", "Pastel Purple",
      "Plum", "Purple", "Taro", "Violet",
    ],
  },
  {
    name: "Red",
    swatches: ["#9B111E", "#722F37"],
    colors: [
      "Burgundy", "Cherry", "Fire", "Fire Red", "Lava", "Merlot",
      "Red", "Red Lipstick", "Red Wine", "Ruby", "Ruby Red", "Scarlet",
      "Strawberry Red",
    ],
  },
  {
    name: "Tan",
    swatches: ["#D2B48C", "#8B7355"],
    colors: ["Camel", "Kaki", "Khaki", "Pecan", "Peanut", "Tan"],
  },
  {
    name: "White",
    swatches: ["#FFFFFF", "#FAFAF0"],
    colors: ["Butter", "Butter Cream", "Snow", "Snow White", "White"],
  },
  {
    name: "Yellow",
    swatches: ["#FFE4A0", "#D4A017"],
    colors: [
      "Bumblebee", "Butter Yellow", "Lemon", "Lemon Yellow", "Mustard",
      "Yellow", "Yellow/White",
    ],
  },
];

// Reverse lookup: catalog color string → family name. Built once at
// module load. Returns null if the color is unknown to any family
// (kept as-is in the WHERE clause for forward-compat with new colors
// added to COLOR_HEX before this file is updated).
const COLOR_TO_FAMILY = (() => {
  const map = new Map<string, string>();
  for (const f of COLOR_FAMILIES) {
    for (const c of f.colors) map.set(c, f.name);
  }
  return map;
})();

/** What family does this catalog color belong to? Returns null if unknown. */
export function colorFamilyOf(color: string): string | null {
  return COLOR_TO_FAMILY.get(color) ?? null;
}

/**
 * Expand a list of `?color=` URL values into the catalog color names
 * for the SQL `IN` clause. Family names expand to their `colors[]`;
 * unknown values pass through unchanged (so legacy bookmarked URLs
 * with raw color names still filter correctly).
 */
export function expandColorFamilies(values: string[]): string[] {
  const out = new Set<string>();
  for (const v of values) {
    const family = COLOR_FAMILIES.find((f) => f.name === v);
    if (family) {
      for (const c of family.colors) out.add(c);
    } else {
      out.add(v);
    }
  }
  return Array.from(out);
}

/**
 * Given the set of catalog colors actually in stock, return the families
 * that have at least one in-stock color. Used to filter the family
 * picker so we don't show "Yellow" if no yellow products exist.
 */
export function familiesPresentIn(catalogColors: string[]): ColorFamily[] {
  const present = new Set<string>();
  for (const c of catalogColors) {
    const fam = COLOR_TO_FAMILY.get(c);
    if (fam) present.add(fam);
  }
  return COLOR_FAMILIES.filter((f) => present.has(f.name));
}

/** Normalize image URL — handles null/undefined, rewrites legacy R2 public URLs to custom domain */
export function fixImgUrl(url: string | null | undefined): string {
  if (!url) return "";
  // Rewrite old R2 public URL to custom domain if any legacy URLs remain
  return url.replace("pub-902716ea73d4467c8cb6d5250584b618.r2.dev", "cdn.eql-apparel.com");
}
